import React, { useState } from 'react';
import { useUI } from '../../contexts/UIContext';
import { apiRequest } from '../../utils/apiUtils';
import {
  BellIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  CheckIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const AdminNotificationTest = () => {
  const { showNotification } = useUI();
  const [creating, setCreating] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    type: 'validation_failure',
    severity: 'medium',
    metadata: '{}'
  });

  const notificationTypes = [
    { value: 'validation_failure', label: 'Validation Failure', icon: ExclamationTriangleIcon },
    { value: 'issue_created', label: 'Issue Created', icon: ExclamationTriangleIcon },
    { value: 'issue_updated', label: 'Issue Updated', icon: InformationCircleIcon },
    { value: 'schema_change', label: 'Schema Change', icon: InformationCircleIcon },
    { value: 'automation_failure', label: 'Automation Failure', icon: ExclamationTriangleIcon },
    { value: 'automation_completion', label: 'Automation Completion', icon: CheckIcon },
    { value: 'system_update', label: 'System Update', icon: InformationCircleIcon }
  ];

  const severityLevels = [
    { value: 'low', label: 'Low', color: 'text-primary-600' },
    { value: 'medium', label: 'Medium', color: 'text-warning-600' },
    { value: 'high', label: 'High', color: 'text-danger-600' }
  ];

  const sampleNotifications = [
    {
      title: 'Validation Failure Detected',
      message: 'Data quality validation failed for table "users". 5 rows failed uniqueness check.',
      type: 'validation_failure',
      severity: 'high',
      metadata: '{"table_name": "users", "connection": "production", "failed_rows": 5}'
    },
    {
      title: 'New Issue Created',
      message: 'A new data quality issue has been created for the sales table.',
      type: 'issue_created',
      severity: 'medium',
      metadata: '{"issue_id": "12345", "table_name": "sales", "priority": "medium"}'
    },
    {
      title: 'Schema Change Detected',
      message: 'New column "updated_at" was added to the products table.',
      type: 'schema_change',
      severity: 'low',
      metadata: '{"table_name": "products", "change_type": "column_added", "column_name": "updated_at"}'
    },
    {
      title: 'Automation Completed',
      message: 'Daily data validation automation completed successfully.',
      type: 'automation_completion',
      severity: 'low',
      metadata: '{"automation_id": "daily_validation", "duration": "2.5 minutes", "status": "success"}'
    }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim() || !formData.message.trim()) {
      showNotification('Title and message are required', 'error');
      return;
    }

    let metadata = {};
    try {
      metadata = JSON.parse(formData.metadata || '{}');
    } catch (error) {
      showNotification('Invalid JSON in metadata field', 'error');
      return;
    }

    try {
      setCreating(true);
      const response = await apiRequest('/notifications', {
        method: 'POST',
        data: {
          title: formData.title.trim(),
          message: formData.message.trim(),
          type: formData.type,
          severity: formData.severity,
          metadata
        }
      });

      if (response.success) {
        showNotification('Notification created successfully!', 'success');
        // Reset form
        setFormData({
          title: '',
          message: '',
          type: 'validation_failure',
          severity: 'medium',
          metadata: '{}'
        });
        
        // Force refresh the notification count in header
        window.location.reload();
      }
    } catch (error) {
      console.error('Error creating notification:', error);
      showNotification('Failed to create notification', 'error');
    } finally {
      setCreating(false);
    }
  };

  const loadSample = (sample) => {
    setFormData(sample);
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-secondary-200">
          <h2 className="text-lg font-medium text-secondary-900 flex items-center">
            <BellIcon className="h-5 w-5 mr-2" />
            Notification Center Test
          </h2>
          <p className="mt-1 text-sm text-secondary-500">
            Create test notifications to verify the in-app notification system is working correctly.
          </p>
        </div>

        <div className="p-6">
          {/* Sample Notifications */}
          <div className="mb-8">
            <h3 className="text-md font-medium text-secondary-900 mb-4">Quick Test Samples</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {sampleNotifications.map((sample, index) => (
                <div
                  key={index}
                  className="p-4 border border-secondary-200 rounded-lg hover:bg-secondary-50 cursor-pointer"
                  onClick={() => loadSample(sample)}
                >
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      {React.createElement(
                        notificationTypes.find(t => t.value === sample.type)?.icon || BellIcon,
                        { className: "h-5 w-5 text-secondary-500" }
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-secondary-900">{sample.title}</p>
                      <p className="text-sm text-secondary-600 truncate">{sample.message}</p>
                      <div className="flex items-center mt-2 space-x-2">
                        <span className="text-xs text-secondary-500">{sample.type}</span>
                        <span className={`text-xs ${severityLevels.find(s => s.value === sample.severity)?.color}`}>
                          {sample.severity}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Create Notification Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Notification Type
                </label>
                <select
                  value={formData.type}
                  onChange={(e) => handleInputChange('type', e.target.value)}
                  className="block w-full border-secondary-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                >
                  {notificationTypes.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Severity Level
                </label>
                <select
                  value={formData.severity}
                  onChange={(e) => handleInputChange('severity', e.target.value)}
                  className="block w-full border-secondary-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                >
                  {severityLevels.map((severity) => (
                    <option key={severity.value} value={severity.value}>
                      {severity.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Title
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                className="block w-full border-secondary-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                placeholder="Enter notification title..."
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Message
              </label>
              <textarea
                value={formData.message}
                onChange={(e) => handleInputChange('message', e.target.value)}
                rows={3}
                className="block w-full border-secondary-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                placeholder="Enter notification message..."
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Metadata (JSON)
              </label>
              <textarea
                value={formData.metadata}
                onChange={(e) => handleInputChange('metadata', e.target.value)}
                rows={3}
                className="block w-full border-secondary-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm font-mono text-sm"
                placeholder='{"key": "value", "table_name": "example"}'
              />
              <p className="mt-1 text-xs text-secondary-500">
                Optional JSON metadata for additional context and navigation
              </p>
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={creating}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
              >
                {creating ? (
                  <>
                    <LoadingSpinner size="xs" className="mr-2" />
                    Creating...
                  </>
                ) : (
                  <>
                    <BellIcon className="h-4 w-4 mr-2" />
                    Create Notification
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Instructions */}
      <div className="bg-primary-50 border border-primary-200 rounded-md p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <InformationCircleIcon className="h-5 w-5 text-primary-400" aria-hidden="true" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-primary-800">Testing Instructions</h3>
            <div className="mt-2 text-sm text-primary-700">
              <ol className="list-decimal list-inside space-y-1">
                <li>First, run the SQL script in Supabase to create the in_app_notifications table</li>
                <li>Click on any sample notification above to load it into the form</li>
                <li>Click "Create Notification" to test the system</li>
                <li>Check the notification icon in the header - you should see a red badge</li>
                <li>Click the notification icon to open the notification center</li>
                <li>Verify that clicking notifications navigates to the appropriate pages</li>
              </ol>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminNotificationTest;